import React from 'react';
import { DashboardLineChart } from './LineChart';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parseISO, isValid } from 'date-fns';

interface TicketVolumeChartProps {
  data: any[];
  title: string;
}

export function TicketVolumeChart({ data, title }: TicketVolumeChartProps) {
  const chartData = React.useMemo(() => {
    console.log("TicketVolumeChart - Processing data:", data?.length || 0, "items");
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log("TicketVolumeChart - No data available, returning empty array");
      return [];
    }
    
    const ticketsByDate = new Map<string, number>();
    
    data.forEach(ticket => {
      if (!ticket) return;
      
      const dateStr = ticket.created || ticket.created_at || ticket.opened || 
                      ticket.opened_at || ticket['Opened'] || ticket['Created'];
      if (!dateStr) return;
      
      try {
        let date: Date;
        
        if (typeof dateStr === 'string') {
          date = parseISO(dateStr);
          // If parseISO failed (invalid ISO format), try with regular Date constructor
          if (!isValid(date)) {
            date = new Date(dateStr);
          }
        } else if (dateStr instanceof Date) {
          date = dateStr;
        } else {
          return; // Skip if not a valid date format
        }
        
        if (!isValid(date)) return; // Skip invalid dates
        
        const formattedDate = format(date, 'MMM dd');
        ticketsByDate.set(
          formattedDate, 
          (ticketsByDate.get(formattedDate) || 0) + 1
        );
      } catch (error) {
        console.error('Error parsing date:', error);
      }
    });

    const result = Array.from(ticketsByDate.entries())
      .map(([date, count]) => ({
        date,
        tickets: count
      }))
      .sort((a, b) => {
        // Sort by month and day
        const [aMonth, aDay] = a.date.split(' ');
        const [bMonth, bDay] = b.date.split(' ');
        
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const aMonthIndex = months.indexOf(aMonth);
        const bMonthIndex = months.indexOf(bMonth);
        
        if (aMonthIndex !== bMonthIndex) {
          return aMonthIndex - bMonthIndex;
        }
        
        return parseInt(aDay) - parseInt(bDay);
      });
      
    console.log("TicketVolumeChart - Generated data points:", result.length);
    return result;
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No ticket data available for analysis
          </div>
        ) : (
          <DashboardLineChart
            title=""
            data={chartData}
            xAxisKey="date"
            lines={[
              { dataKey: "tickets", stroke: "#8884d8", name: "Tickets" }
            ]}
          />
        )}
      </CardContent>
    </Card>
  );
}
